import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, X, Send, ArrowRight, Mail, Plus, Check } from "lucide-react";
import { useMaintenance } from "@/components/MaintenanceLayout";
import MachineIdentity, { MachineMark } from "@/components/maintenance/MachineMark";
import { categoryLabel } from "@/components/maintenance/lib";

// Report a breakdown — the shop floor's one form. Tap the machine tile, pick
// the closest problem, answer one plain question, send. No dropdowns, no
// typing machine IDs, no "your name" field: the reporter is stamped from the
// login (MAINTENANCE_MODULE_PLAN.md §3b-1, §8d).
//
// Machines that already have an open ticket are greyed WITH the reason — and
// the server still enforces one open ticket per machine (409), so two people
// reporting at once can't fork a duplicate.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReportBreakdownLightbox({ open, onOpenChange, onReported }) {
  const navigate = useNavigate();
  const { reasons = [], reasonOther = "Something else…", refresh } = useMaintenance() || {};
  const [machines, setMachines] = useState([]);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(null);
  const [reason, setReason] = useState("");
  const [custom, setCustom] = useState("");
  const [note, setNote] = useState("");
  const [stopped, setStopped] = useState(true);
  const [busy, setBusy] = useState(false);
  // Email-on-report: the standing list arrives pre-ticked; the reporter can
  // untick people or add colleagues for THIS breakdown only.
  const [notif, setNotif] = useState({ enabled: false, users: [], other_addresses: [] });
  const [extra, setExtra] = useState([]);          // people added for this one
  const [unticked, setUnticked] = useState(() => new Set());
  const [people, setPeople] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addQuery, setAddQuery] = useState("");
  const searchRef = useRef(null);
  const stepRef = useRef(null);

  const close = useCallback(() => onOpenChange?.(false), [onOpenChange]);

  useEffect(() => {
    if (!open) return;
    setQuery(""); setPicked(null); setReason(""); setCustom(""); setNote(""); setStopped(true);
    setExtra([]); setUnticked(new Set()); setAddOpen(false); setAddQuery("");
    axios.get(`${API}/maintenance/alerts/report-recipients`)
      .then((r) => setNotif(r.data || { enabled: false, users: [], other_addresses: [] }))
      .catch(() => setNotif({ enabled: false, users: [], other_addresses: [] }));
    axios.get(`${API}/maintenance/machines`)
      .then((r) => setMachines(r.data?.machines || []))
      .catch(() => toast.error("Could not load the machine list."));
    const t = setTimeout(() => searchRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    // The page behind must not scroll while the sheet is up.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, close]);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const out = new Map();
    for (const m of machines) {
      if (q && !(`${m.name} ${m.machine_no} ${m.make}`.toLowerCase().includes(q))) continue;
      const key = m.category || "Other machines";
      if (!out.has(key)) out.set(key, []);
      out.get(key).push(m);
    }
    return [...out.entries()];
  }, [machines, query]);

  const finalReason = reason === reasonOther ? custom.trim() : reason;

  const notifying = notif.enabled && stopped;
  const notifyList = useMemo(() => [
    ...notif.users.map((u) => ({ key: u.id, label: u.name, userId: u.id })),
    ...extra.map((u) => ({ key: u.id, label: u.name, userId: u.id })),
    ...notif.other_addresses.map((e) => ({ key: `addr:${e}`, label: e, address: e })),
  ], [notif, extra]);
  const tickedCount = notifyList.filter((n) => !unticked.has(n.key)).length;
  const toggleTick = (key) => setUnticked((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const addMatches = useMemo(() => {
    if (!people) return [];
    const taken = new Set(notifyList.map((n) => n.userId).filter(Boolean));
    const q = addQuery.trim().toLowerCase();
    return people.filter((p) => !taken.has(p.id))
      .filter((p) => !q || `${p.name} ${p.role}`.toLowerCase().includes(q)).slice(0, 6);
  }, [people, notifyList, addQuery]);
  const openAdd = () => {
    setAddOpen(true);
    if (!people) axios.get(`${API}/users/mentionables`).then((r) => setPeople(r.data || [])).catch(() => setPeople([]));
  };
  const ready = !!picked && finalReason.length > 0;

  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/maintenance/tickets`, {
        machine_no: picked.machine_no,
        reason: finalReason,
        note: note.trim(),
        stoppage: stopped ? "stopped" : "degraded",
        ...(notifying ? {
          notify_user_ids: notifyList.filter((n) => n.userId && !unticked.has(n.key)).map((n) => n.userId),
          notify_other_addresses: notifyList.filter((n) => n.address && !unticked.has(n.key)).map((n) => n.address),
        } : {}),
      });
      toast.success(notifying && tickedCount > 0
        ? `${picked.name} reported — ${tickedCount} ${tickedCount === 1 ? "person" : "people"} emailed`
        : `${picked.name} reported — maintenance can see it now`);
      close();
      refresh?.();
      onReported?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409) {
        toast.info(typeof d === "object" ? d.message : "That machine is already reported — it's in the pipeline.");
        // Re-read so the tile greys out for this user too.
        axios.get(`${API}/maintenance/machines`).then((r) => setMachines(r.data?.machines || [])).catch(() => {});
        setPicked(null);
      } else {
        toast.error(typeof d === "string" ? d : d?.message || "That didn't go through.");
      }
    } finally {
      setBusy(false);
    }
  };

  const pick = (m) => {
    setPicked(m);
    setTimeout(() => stepRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 30);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto px-4 py-10"
      style={{ background: "rgba(20,15,10,0.45)" }}
      onClick={close}
      data-testid="mt-report-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Report a breakdown"
    >
      <div
        className="rl-frost rl-hero-glass w-full max-w-[980px] rounded-[18px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-[22px] pt-[18px] pb-3.5 flex items-center justify-between gap-3 flex-wrap border-b border-[hsl(var(--border))]">
          <div>
            <h3 className="text-[17px] font-extrabold" style={{ fontFamily: "var(--rl-font-heading), sans-serif" }}>Report a breakdown</h3>
            <div className="text-[11.5px] text-[hsl(var(--muted-foreground))] mt-[3px]">Tap the machine that has the problem</div>
          </div>
          <div className="flex items-center gap-2.5">
            <div className="rl-carved flex items-center gap-2 h-9 px-3.5 rounded-full bg-[hsl(var(--muted))]/50">
              <Search className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search machine…"
                data-testid="mt-machine-search"
                className="bg-transparent outline-none text-[13px] w-[150px] sm:w-[220px] placeholder:text-[hsl(var(--muted-foreground))]"
              />
            </div>
            <button
              onClick={close}
              aria-label="Close"
              data-testid="mt-report-close"
              className="rl-frost-btn w-8 h-8 rounded-full flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Category-grouped tiles. One column on phones — production reports
            from the shop floor (§8e). */}
        <div
          className="grid gap-2 px-[22px] py-4 max-h-[320px] overflow-y-auto grid-cols-1 sm:[grid-template-columns:repeat(auto-fill,minmax(205px,1fr))]"
          data-testid="mt-machine-grid"
        >
          {grouped.length === 0 && (
            <p className="col-span-full text-[12px] text-[hsl(var(--muted-foreground))] py-6 text-center">
              No machine matches “{query}”.
            </p>
          )}
          {grouped.map(([cat, list]) => (
            <React.Fragment key={cat}>
              <div className="col-span-full text-[9.5px] font-extrabold uppercase tracking-[0.1em] text-[hsl(var(--muted-foreground))]/80 mt-1">
                {categoryLabel(cat)}
              </div>
              {list.map((m) => {
                const down = !!m.open_ticket;
                const isPicked = picked?.machine_no === m.machine_no;
                return (
                  <button
                    key={m.machine_no}
                    onClick={() => !down && pick(m)}
                    disabled={down}
                    title={down ? `Already reported — ${m.open_ticket.reason}` : undefined}
                    data-testid={`mt-machine-tile-${m.machine_no}`}
                    className={`rl-mach-tile rl-tile ${isPicked ? "is-selected" : ""}`}
                  >
                    <MachineMark make={m.make} size={30} />
                    <div className="min-w-0 flex-1 text-left">
                      <div className="text-[12.5px] font-bold leading-[1.15] truncate">{m.name}</div>
                      <div className="text-[9.5px] text-[hsl(var(--muted-foreground))] mt-[2px] truncate">
                        {down ? "Already down — in pipeline" : m.make}
                      </div>
                    </div>
                    <span className="rl-me-chip rl-carved shrink-0">{m.machine_no}</span>
                  </button>
                );
              })}
            </React.Fragment>
          ))}
        </div>

        {picked && (
          <div ref={stepRef} className="border-t border-[hsl(var(--border))] px-[22px] pt-4 pb-5" data-testid="mt-report-step2">
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">Reporting:</span>
              <MachineIdentity machine={picked} size={26} showCategory={false} />
            </div>

            <div className="text-[10px] font-bold uppercase tracking-[0.09em] text-[hsl(var(--muted-foreground))] mt-4">
              What's wrong? — pick the closest one
            </div>
            <div className="flex flex-wrap gap-[7px] mt-2.5">
              {reasons.map((r) => (
                <button
                  key={r}
                  onClick={() => setReason(r)}
                  data-testid={`mt-problem-chip-${r === reasonOther ? "other" : r.slice(0, 12)}`}
                  className={`rl-prob-chip rl-pill ${reason === r ? "is-picked" : ""}`}
                >
                  {r}
                </button>
              ))}
            </div>
            {reason === reasonOther && (
              <input
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="Describe the problem in your words…"
                data-testid="mt-problem-custom"
                autoFocus
                className="mt-2 w-full max-w-[420px] h-9 rounded-[10px] border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3.5 text-[13px] outline-none focus:border-[hsl(var(--primary))]/60"
              />
            )}

            <div className="text-[10px] font-bold uppercase tracking-[0.09em] text-[hsl(var(--muted-foreground))] mt-4">
              Anything else maintenance should know? <span className="font-medium normal-case tracking-normal">(optional)</span>
            </div>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Alarm 224 on start — chuck will not clamp…"
              data-testid="mt-report-note"
              className="mt-2 w-full max-w-[520px] h-9 rounded-[10px] border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3.5 text-[13px] outline-none focus:border-[hsl(var(--primary))]/60"
            />

            <div className="text-[10px] font-bold uppercase tracking-[0.09em] text-[hsl(var(--muted-foreground))] mt-4">
              Has the machine stopped completely?
            </div>
            <div className="flex gap-2 mt-2.5 flex-col sm:flex-row">
              <button
                onClick={() => setStopped(true)}
                data-testid="mt-stopped-yes"
                className={`rl-stop-opt rl-tile ${stopped ? "is-picked" : ""}`}
              >
                <div className={`font-bold text-[13px] ${stopped ? "rl-tx-red" : ""}`}>Yes — stopped</div>
                <div className="text-[10.5px] text-[hsl(var(--muted-foreground))] mt-[2px]">Not producing at all</div>
              </button>
              <button
                onClick={() => setStopped(false)}
                data-testid="mt-stopped-no"
                className={`rl-stop-opt rl-tile ${!stopped ? "is-picked" : ""}`}
              >
                <div className="font-bold text-[13px]">No — running with the problem</div>
                <div className="text-[10.5px] text-[hsl(var(--muted-foreground))] mt-[2px]">Still cutting, needs attention</div>
              </button>
            </div>

            {notifying && (
              <div className="mt-4" data-testid="mt-report-notifying">
                <div className="flex items-center gap-1.5 flex-wrap text-[11.5px] text-[hsl(var(--muted-foreground))]">
                  <Mail className="w-3.5 h-3.5" />
                  <span className="mr-1">Notifying</span>
                  {notifyList.map((n) => {
                    const on = !unticked.has(n.key);
                    return (
                      <button
                        key={n.key}
                        onClick={() => toggleTick(n.key)}
                        title={on ? "Tap to leave them out this time" : "Tap to include them"}
                        data-testid={`mt-notify-${n.key}`}
                        className={`rl-pill inline-flex items-center gap-1 h-7 px-2.5 rounded-full border text-[11.5px] transition-colors ${
                          on ? "border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] font-semibold"
                             : "border-dashed border-[hsl(var(--border))] bg-transparent text-[hsl(var(--muted-foreground))] line-through"}`}
                      >
                        {on && <Check className="w-3 h-3" />}{n.label}
                      </button>
                    );
                  })}
                  {notifyList.length === 0 && <span className="italic">nobody is on the list yet</span>}
                  {!addOpen && (
                    <button
                      onClick={openAdd}
                      data-testid="mt-notify-add"
                      className="inline-flex items-center gap-1 h-7 px-2 rounded-full text-[11.5px] hover:text-[hsl(var(--foreground))]"
                    >
                      <Plus className="w-3 h-3" /> add someone
                    </button>
                  )}
                </div>
                {addOpen && (
                  <div className="mt-2 max-w-[360px]">
                    <input
                      autoFocus
                      value={addQuery}
                      onChange={(e) => setAddQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") { setAddOpen(false); setAddQuery(""); }
                        if (e.key === "Enter" && addMatches[0]) { e.preventDefault(); setExtra((x) => [...x, addMatches[0]]); setAddQuery(""); setAddOpen(false); }
                      }}
                      placeholder="Type a name…"
                      data-testid="mt-notify-add-input"
                      className="w-full h-8 rounded-[10px] border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-[12.5px] outline-none focus:border-[hsl(var(--primary))]/60"
                    />
                    <div className="mt-1 rounded-[10px] border border-[hsl(var(--border))] bg-[hsl(var(--card))] py-1">
                      {people === null && <div className="px-3 py-1.5 text-[12px] text-[hsl(var(--muted-foreground))]">Loading people…</div>}
                      {people !== null && addMatches.length === 0 && <div className="px-3 py-1.5 text-[12px] text-[hsl(var(--muted-foreground))]">Nobody else matches</div>}
                      {addMatches.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => { setExtra((x) => [...x, p]); setAddQuery(""); setAddOpen(false); }}
                          data-testid={`mt-notify-pick-${p.id}`}
                          className="w-full text-left px-3 py-1.5 hover:bg-[hsl(var(--muted))]/60 flex items-baseline gap-2"
                        >
                          <span className="text-[12.5px] font-semibold truncate">{p.name || p.email}</span>
                          <span className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{p.role}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center gap-3 mt-[18px] flex-wrap">
              <button
                onClick={submit}
                disabled={!ready || busy}
                title={ready ? undefined : "Pick what's wrong first"}
                data-testid="mt-report-submit"
                className="rl-lit-btn inline-flex items-center gap-2 h-[38px] px-5 rounded-full bg-orange-600 hover:bg-orange-500 text-white text-[13px] font-bold transition-colors disabled:opacity-45 disabled:cursor-not-allowed"
              >
                <Send className="w-[15px] h-[15px]" /> {busy ? "Sending…" : "Send to maintenance"}
              </button>
              <span className="text-[11px] text-[hsl(var(--muted-foreground))] italic">
                Reported by you automatically — no name to type
              </span>
            </div>

            {/* Not everything is a breakdown. One quiet line out to the place
                the small stuff belongs, carrying the machine you picked. */}
            <button
              onClick={() => {
                close();
                navigate("/maintenance/requests", { state: { machineNo: picked?.machine_no || null, focus: true } });
              }}
              data-testid="mt-report-to-request"
              className="mt-4 inline-flex items-center gap-1.5 text-[11.5px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] underline underline-offset-2"
            >
              Not a breakdown? Raise a request instead <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
